import { useState, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, fmtMoney } from "@/lib/api";
import { useMasters, opt, vehicleOptions, matchVehicle, partiesByRole, partiesByRoles, useLabourRate } from "@/lib/useMasters";
import { useOnline } from "@/lib/useOnline";
import { PageHeader, Card, Field, SelectInput, TextInput, NumberInput, TextArea, SectionTitle, SuggestInput } from "@/components/kit";
import TxnRecent, { inwardColumns, inwardFields } from "@/components/TxnRecent";
import { Loader2, WifiOff, ArrowDownToLine } from "lucide-react";

const CHARGE_WHO = [
  { value: "COMPANY", label: "Company bear" },
  { value: "STOCK_OWNER", label: "Stock Owner" },
  { value: "SENDER", label: "Sender" },
];

function resolveChargeParty(who, f) {
  if (who === "STOCK_OWNER") return f.stock_owner_id ? +f.stock_owner_id : null;
  if (who === "SENDER") return f.sender_id ? +f.sender_id : null;
  return null;
}

export default function Inward() {
  const m = useMasters();
  const online = useOnline();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().slice(0, 5),
    warehouse_id: "", stack_id: "", vehicle_id: "", vehicle_number: "", driver: "",
    stock_owner_id: "", sender_id: "", source_type: "", source_reference: "",
    stock_item_id: "", variety: "", grade: "", bag_type_id: "",
    bags: "", net_weight: "",
    remarks: "",
    lab_contractor_id: "", lab_rate: "", bag_lab_rate: "",
    expected_dala: "", dala_from_vehicle: "", dala_recover_from: "STOCK_OWNER",
    torn_qty: "", spill_qty: "", bag_charge_to: "STOCK_OWNER",
  });

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const stockOwnerOpts = opt(partiesByRole(m.parties, "Stock Owner"), (p) => `${p.name} (${p.code})`);
  const senderOpts = opt(partiesByRoles(m.parties, ["Sender", "Buyer", "Seller"]), (p) => `${p.name} (${p.code})`);
  const labourOpts = opt(partiesByRole(m.parties, "Labour Contractor"), (p) => `${p.name} (${p.code})`);
  const stackOpts = opt(
    m.stacks.filter((s) => String(s.warehouse_id) === String(f.warehouse_id)),
    (s) => {
      const occ = s.occupancy || {};
      const pct = occ.space_used_pct ?? occ.utilisation;
      const tag = occ.stack_complete ? " · COMPLETE" : "";
      return `${s.code} — ${pct != null ? pct + "% space" : ""}${tag}`;
    }
  );

  const { data: labRate } = useLabourRate(f.lab_contractor_id, f.bag_type_id);
  useEffect(() => {
    if (!f.lab_contractor_id || !f.bag_type_id) {
      setF((s) => (s.lab_rate || s.bag_lab_rate ? { ...s, lab_rate: "", bag_lab_rate: "" } : s));
      return;
    }
    if (labRate?.found) {
      setF((s) => ({
        ...s,
        lab_rate: String(labRate.unloading_rate ?? ""),
        bag_lab_rate: String(labRate.replace_fill_rate ?? ""),
      }));
    } else if (labRate && !labRate.found) {
      setF((s) => ({ ...s, lab_rate: "", bag_lab_rate: "" }));
    }
  }, [labRate, f.lab_contractor_id, f.bag_type_id]);

  const vehicleBags = Number(f.bags || 0);
  const tornQty = Number(f.torn_qty || 0);
  const spillQty = Number(f.spill_qty || 0);
  const stockBags = vehicleBags + spillQty;
  const chargeBags = tornQty + spillQty;
  const bagType = useMemo(() => (m.bagTypes || []).find((b) => String(b.id) === String(f.bag_type_id)), [m.bagTypes, f.bag_type_id]);
  const bagRate = Number(bagType?.charge_rate || 0);
  const bagChargeAmt = chargeBags * bagRate;
  const labRateNum = Number(f.lab_rate || 0);
  const bagLabRateNum = Number(f.bag_lab_rate || 0);
  const bagLabourAmt = chargeBags * bagLabRateNum;
  const labourWork = useMemo(() => vehicleBags * labRateNum, [vehicleBags, labRateNum]);
  const dalaShort = useMemo(() => Math.max(Number(f.expected_dala || 0) - Number(f.dala_from_vehicle || 0), 0), [f.expected_dala, f.dala_from_vehicle]);

  const submit = async (e) => {
    e.preventDefault();
    if (!online) return toast.error("You are offline. Stock transactions require a live connection.");
    if (!f.warehouse_id || !f.stack_id || !f.stock_owner_id || !f.stock_item_id || !f.bags) {
      return toast.error("Please fill warehouse, stack, stock owner, item and bags in vehicle.");
    }
    if (tornQty > vehicleBags) {
      return toast.error("Torn bags cannot exceed bags in the vehicle.");
    }
    if ((tornQty || spillQty) && !f.bag_type_id) {
      return toast.error("Select a bag type so replacement bags can be charged at the fixed rate.");
    }
    if (f.lab_contractor_id && !f.lab_rate) {
      return toast.error("No unloading rate in the contractor agreement for this bag type. Set it under Masters → Labour Rates.");
    }
    if (dalaShort > 0 && f.dala_recover_from === "SENDER" && !f.sender_id) {
      return toast.error("Select a sender to recover the dala shortfall from them.");
    }
    if (chargeBags > 0 && f.bag_charge_to === "SENDER" && !f.sender_id) {
      return toast.error("Select a sender to charge replacement bags to them.");
    }
    if (chargeBags > 0 && !f.lab_contractor_id) {
      return toast.error("Select a labour contractor — torn/spillage need bag labour (replace/fill) as well as bag material.");
    }
    if (chargeBags > 0 && !bagLabRateNum) {
      return toast.error("No Replace/Fill rate in the contractor agreement for this bag type. Set it under Masters → Labour Rates.");
    }
    const chargedTo = resolveChargeParty(f.bag_charge_to, f);
    const contractorId = f.lab_contractor_id ? +f.lab_contractor_id : null;
    const body = {
      date: f.date, time: f.time,
      warehouse_id: +f.warehouse_id, stack_id: +f.stack_id,
      vehicle_id: matchVehicle(m.vehicles, f.vehicle_id, f.vehicle_number)?.id || null, driver: f.driver,
      stock_owner_id: +f.stock_owner_id, sender_id: f.sender_id ? +f.sender_id : null,
      source_type: f.source_type, source_reference: f.source_reference,
      stock_item_id: +f.stock_item_id, variety: f.variety, grade: f.grade,
      bag_type_id: f.bag_type_id ? +f.bag_type_id : null,
      bags: stockBags, net_weight: +f.net_weight || 0, remarks: f.remarks,
      labour: f.lab_contractor_id
        ? {
            contractor_id: contractorId,
            quantity: vehicleBags, rate: labRateNum,
            work_charged_to_id: +f.stock_owner_id,
            expected_dala: +f.expected_dala || 0, dala_from_vehicle: +f.dala_from_vehicle || 0,
            dala_recovery_party_id: resolveChargeParty(f.dala_recover_from, f),
            dala_recovery_from: f.dala_recover_from,
          }
        : null,
      bags_detail: [
        tornQty && {
          charge_type: "Torn", quantity: tornQty, rate: bagRate,
          labour_rate: bagLabRateNum, labour_contractor_id: contractorId,
          charged_to_id: chargedTo, charge_to_role: f.bag_charge_to, reason: "Torn bags replaced",
        },
        spillQty && {
          charge_type: "Spillage", quantity: spillQty, rate: bagRate,
          labour_rate: bagLabRateNum, labour_contractor_id: contractorId,
          charged_to_id: chargedTo, charge_to_role: f.bag_charge_to, reason: "Bags filled from spillage",
        },
      ].filter(Boolean),
    };
    setSubmitting(true);
    try {
      const { data } = await api.post("/txn/inward", body);
      toast.success(`Inward ${data.inward_number} posted → ${data.lot_number}${data.new_lot ? " (new lot)" : ""}`);
      qc.invalidateQueries({ queryKey: ["reg-labour"] });
      qc.invalidateQueries();
      navigate("/registers");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (m.loading)
    return <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-emerald-700" size={32} /></div>;

  return (
    <div data-testid="inward-page">
    <form onSubmit={submit}>
      <PageHeader
        title="Inward Entry"
        subtitle="Single-stage atomic goods receipt — posts stock, lot, labour, dala & charges together"
        action={
          <button
            data-testid="inward-submit"
            type="submit"
            disabled={submitting || !online}
            className="flex h-11 items-center gap-2 rounded-full bg-emerald-800 px-6 font-semibold text-white transition-colors hover:bg-emerald-900 active:scale-[0.99] disabled:opacity-60"
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <ArrowDownToLine size={18} />}
            Post Inward
          </button>
        }
      />

      {!online && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <WifiOff size={16} /> You are offline. Posting is disabled until connection is restored.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5">
            <SectionTitle>Transaction & Location</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Date" required><TextInput type="date" value={f.date} onChange={set("date")} data-testid="inward-date" /></Field>
              <Field label="Time"><TextInput type="time" value={f.time} onChange={set("time")} /></Field>
              <Field label="Warehouse" required>
                <SelectInput options={opt(m.warehouses, (w) => `${w.name}`)} value={f.warehouse_id} onChange={(e) => setF((s) => ({ ...s, warehouse_id: e.target.value, stack_id: "" }))} data-testid="inward-warehouse" />
              </Field>
              <Field label="Stack" required>
                <SelectInput options={stackOpts} value={f.stack_id} onChange={set("stack_id")} data-testid="inward-stack" placeholder={f.warehouse_id ? "Select stack" : "Select warehouse first"} />
              </Field>
              <Field label="Vehicle">
                <SuggestInput
                  data-testid="inward-vehicle"
                  placeholder="Type vehicle number"
                  value={f.vehicle_number}
                  options={vehicleOptions(m.vehicles)}
                  onChange={(v) => setF((s) => ({ ...s, vehicle_number: v, vehicle_id: "" }))}
                  onSelect={(o) => setF((s) => ({ ...s, vehicle_id: o.value, vehicle_number: o.label, driver: o.raw?.driver || s.driver }))}
                />
              </Field>
              <Field label="Driver"><TextInput value={f.driver} onChange={set("driver")} placeholder="Driver name" /></Field>
            </div>
          </Card>

          <Card className="p-5">
            <SectionTitle>Ownership & Source</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Stock Owner" required>
                <SelectInput options={stockOwnerOpts} value={f.stock_owner_id} onChange={set("stock_owner_id")} data-testid="inward-owner" />
              </Field>
              <Field label="Sender / Source">
                <SelectInput options={senderOpts} value={f.sender_id} onChange={set("sender_id")} data-testid="inward-sender" />
              </Field>
              <Field label="Source Type"><TextInput value={f.source_type} onChange={set("source_type")} placeholder="e.g. Mandi purchase" /></Field>
              <Field label="Source Reference"><TextInput value={f.source_reference} onChange={set("source_reference")} placeholder="Doc / bill no." /></Field>
            </div>
            <p className="mt-2 text-xs text-stone-400">Stock Owner and Sender may be different parties — both identities are retained.</p>
          </Card>

          <Card className="p-5">
            <SectionTitle>Stock Details</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Stock Item" required>
                <SelectInput options={opt(m.items, (i) => i.name)} value={f.stock_item_id} onChange={set("stock_item_id")} data-testid="inward-item" />
              </Field>
              <Field label="Variety"><TextInput value={f.variety} onChange={set("variety")} /></Field>
              <Field label="Bag Type">
                <SelectInput options={opt(m.bagTypes, (b) => b.name)} value={f.bag_type_id} onChange={set("bag_type_id")} />
              </Field>
              <Field label="Bags in vehicle" required><NumberInput value={f.bags} onChange={set("bags")} data-testid="inward-bags" /></Field>
              <Field label="Net Weight (Qtl)" required><NumberInput value={f.net_weight} onChange={set("net_weight")} data-testid="inward-net" /></Field>
            </div>
            <div className="mt-3 rounded-lg bg-stone-50 px-3 py-2 text-sm flex justify-between">
              <span className="text-stone-600">Stock bags after inward</span>
              <span className="font-semibold">{stockBags || 0}</span>
            </div>
            <p className="mt-1 text-xs text-stone-400">Vehicle bags + bags filled from spillage. Torn replacements do not add extra stock.</p>
          </Card>

          <Card className="p-5">
            <SectionTitle>Torn & Spillage Bags</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Torn (replaced)">
                <NumberInput value={f.torn_qty} onChange={set("torn_qty")} data-testid="bag-torn-qty" />
              </Field>
              <Field label="Filled from spillage">
                <NumberInput value={f.spill_qty} onChange={set("spill_qty")} data-testid="bag-spill-qty" />
              </Field>
            </div>
            <div className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-stone-600">Bag material ({chargeBags} × {fmtMoney(bagRate)})</span>
                <span className="font-semibold">{fmtMoney(bagChargeAmt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-600">Bag labour replace/fill ({chargeBags} × {fmtMoney(bagLabRateNum)})</span>
                <span className="font-semibold">{fmtMoney(bagLabourAmt)}</span>
              </div>
              <div className="flex justify-between border-t border-emerald-100 pt-1">
                <span className="text-stone-600">Total torn/spill charges</span>
                <span className="font-semibold">{fmtMoney(bagChargeAmt + bagLabourAmt)}</span>
              </div>
              <div className="text-xs text-stone-500">
                {bagType ? `${bagType.name} fixed rate` : "Select a bag type for the fixed rate"}
                {chargeBags > 0 ? ` · ${tornQty} torn + ${spillQty} spillage` : ""}
              </div>
            </div>
            <div className="mt-3">
              <Field label="Bag charges paid by">
                <SelectInput options={CHARGE_WHO} value={f.bag_charge_to} onChange={set("bag_charge_to")} placeholder="" data-testid="inward-bag-charge-to" />
              </Field>
            </div>
          </Card>
        </div>

        {/* Labour & Dala sidebar */}
        <div className="space-y-6">
          <Card className="p-5">
            <SectionTitle>Unloading Labour & Dala</SectionTitle>
            <div className="space-y-4">
              <Field label="Labour Contractor">
                <SelectInput options={labourOpts} value={f.lab_contractor_id} onChange={set("lab_contractor_id")} />
              </Field>
              {f.lab_contractor_id && f.bag_type_id && labRate && !labRate.found && (
                <p className="text-xs text-amber-700">No unloading rate for this contractor and bag type. Set it under Masters → Labour Rates.</p>
              )}
              {f.lab_contractor_id && !f.bag_type_id && (
                <p className="text-xs text-stone-400">Select a bag type to load the agreement rate.</p>
              )}
              <div className="rounded-lg bg-emerald-50 p-3 text-sm">
                <div className="flex justify-between"><span className="text-stone-600">Work payment</span><span className="font-semibold">{fmtMoney(labourWork)}</span></div>
                <div className="mt-1 text-xs text-stone-500">{vehicleBags || 0} bags unloaded × ₹{f.lab_rate || 0} (agreement) · charged to stock owner</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Expected Dala"><NumberInput value={f.expected_dala} onChange={set("expected_dala")} data-testid="inward-dala-exp" /></Field>
                <Field label="From Vehicle"><NumberInput value={f.dala_from_vehicle} onChange={set("dala_from_vehicle")} data-testid="inward-dala-veh" /></Field>
              </div>
              <div className="rounded-lg bg-amber-50 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-stone-600">Shortfall (SRM pays labour)</span><span className="font-semibold text-amber-800">{fmtMoney(dalaShort)}</span></div>
                <div className="text-xs text-stone-500">Labour still receives full dala.</div>
              </div>
              <Field label="Recover Shortfall From">
                <SelectInput options={CHARGE_WHO} value={f.dala_recover_from} onChange={set("dala_recover_from")} placeholder="" data-testid="inward-dala-recover" />
              </Field>
            </div>
          </Card>

          <Card className="p-5">
            <SectionTitle>Remarks</SectionTitle>
            <TextArea rows={3} value={f.remarks} onChange={set("remarks")} placeholder="Optional notes" />
          </Card>
        </div>
      </div>
    </form>
    <TxnRecent kind="inward" title="Recent Inwards" columns={inwardColumns} editFields={inwardFields} />
    </div>
  );
}
